"use client";

import { useInspectorMode } from "@utils/ContentfulUtils";
import { useLocale } from "next-intl";
import Image from "next/image";
import { getImageProps } from "next/image";
import React, { useEffect, useRef } from "react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import useMediaQuery from "@/hooks/useMediaQuery";
import { BaseEntry, CustomLink, Media } from "@/types";
import cn from "@/utils/cn";
import { getLink } from "@/utils/linkUtils";

interface HeroProjectCommunityCard extends BaseEntry {
  video: Media;
  video_mobile?: Media;
  videoCoverImage: Media;
  videoCoverImageMobile?: Media;
  logo?: Media;
  videoTitle?: string;
  ctaLink1?: CustomLink;
  ctaLink2?: CustomLink;
}

interface Props {
  entry: HeroProjectCommunityCard;
  h1ID: string;
  animation?: boolean;
  singleSlide?: boolean;
}

export default function HeroProjectCommunityCard({ entry, h1ID, animation, singleSlide }: Props) {
  const inspectorMode = useInspectorMode(entry.id);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isMobile = useMediaQuery("(max-width: 600px)");
  const locale = useLocale();
  const underlineButtonClasses = cn("btn btn-tertiary-white btn-medium font-medium border-0 md:p-0");
  const heroClasses = cn("btn btn-primary-white block !border-white hover:!border-0 text-center btn-medium font-medium border-0");

  useEffect(() => {
    const mainContainer = document.querySelector("main");
    if (mainContainer && animation) {
      mainContainer.style.display = "flex";
      mainContainer.style.flexDirection = "column";
    }
  }, [animation]);
  const poster = getImageProps({
    alt: "",
    width: isMobile ? 768 : 1024,
    height: isMobile ? 1024 : 768,
    quality: 80,
    src: entry?.videoCoverImageMobile?.url && isMobile ? entry.videoCoverImageMobile.url : isDesktop ? entry.videoCoverImage.url : "",
  });
  return (
    <div className="relative flex justify-center h-svh">
      <div className="absolute z-10 w-full h-full img-overlay-homepage-hero"></div>
      <video
        className="videoHero"
        // poster={entry.videoCoverImage.url}
        poster={poster.props.src}
        muted
        autoPlay
        loop
        playsInline
        {...inspectorMode.getProps("video")}
      >
        {isMobile && entry.video_mobile && <source media="all and (orientation: portrait)" src={entry.video_mobile.url} type="video/mp4" />}
        {isDesktop && <source media={entry.video_mobile ? "all and (orientation: landscape)" : undefined} src={entry.video.url} type="video/mp4" />}
      </video>
      <div className={cn("absolute bottom-0 z-20 left-1/2 -translate-x-1/2 mb-[40px] w-full", animation && singleSlide && "-translate-y-1/2")}>
        <div className="flex flex-col items-center">
          {entry.logo && (
            <Image
              src={entry.logo.url}
              alt={entry.logo.title}
              width={entry.logo.width}
              height={entry.logo.height}
              {...inspectorMode.getProps("logo")}
            />
          )}
          {entry.videoTitle && (
            <span
              className="font-medium text-white uppercase  font-primary text-l md:text-xl md:whitespace-nowrap text-center px-xxs md:px-0"
              {...inspectorMode.getProps("videoTitle")}
            >
              {h1ID && h1ID == entry.id ? <h1>{entry.videoTitle}</h1> : entry.videoTitle}
              {/* {entry.videoTitle} */}
            </span>
          )}
          <div className={cn(entry.ctaLink1 && entry.ctaLink2 && "flex flex-col gap-m md:flex-row md:gap-l items-center", "mt-l ")}>
            {entry.ctaLink1 && (
              <Button
                href={getLink(entry.ctaLink1, locale)}
                className={heroClasses}
                type="button"
                size={ButtonSize.MEDIUM}
                variant={ButtonVariant.PRIMARY}
                theme={ButtonTheme.WHITE}
                {...inspectorMode.getProps("ctaLink1")}
              >
                {entry.ctaLink1.label}
              </Button>
            )}
            {entry.ctaLink2 && (
              <Button
                type="button"
                className={underlineButtonClasses}
                href={getLink(entry.ctaLink2, locale)}
                size={ButtonSize.MEDIUM}
                variant={ButtonVariant.TERTIARY}
                theme={ButtonTheme.WHITE}
                {...inspectorMode.getProps("ctaLink2")}
              >
                {entry.ctaLink2.label}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
